import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Device } from '@capacitor/device';
import {
  IonContent,
  IonPage,
  IonItem,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardContent,
  IonCardTitle,
  IonIcon,
  IonModal,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  useIonToast,
  IonLoading,
  IonSkeletonText,
  IonRow,
  useIonViewWillLeave,
  useIonRouter,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/react';
import { MenuItem, Select } from '@material-ui/core';
import { barcodeOutline, close, constructOutline } from 'ionicons/icons';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Clock from '../../assets/Clock.svg';
import CreateAsset from '../../assets/CreateAsset.svg';
import CreateComp from '../../assets/CreateComp.svg';
import ViewComp from '../../assets/ViewComp.svg';
import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import ScanButton from '../../components/ScanButton';
import { createAssetFields } from '../../models/createAssetFields';
import { AppContext } from '../../providers/AppContext';
import { constant, getStatusColor, isFormDataEmpty, requestPermissions } from '../../utils';

const Assets: React.FC = () => {
  const isMounted = useRef(true);
  const { state } = useContext(AppContext);

  const { t } = useTranslation();
  const [present] = useIonToast();
  const router = useIonRouter();

  const [highlights, setHighlights] = useState<any>([]);
  const [pname, setPname] = useState('');
  const [fabModal, setFabModal] = useState(false);
  const [loaderM, setLoaderM] = useState(false);
  const [loader, setLoader] = useState(false);

  // New State variables for pagination and searching
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [projectList, setProjectList] = useState<any[]>([]);

  const [_data] = useState<any>(createAssetFields);
  // const [scanToggle, setScanToggle] = useState(false);

  const [primaryData, setPrimaryData] = useState<any>({});

  const [update, setUpdate] = useState<any>({
    status: '',
    asset_state: '',
    brand: '',
    model: '',
    year: '',
    comment: '',
    // asset_component: [],
    attachment: {},
    barcode_id: '',
  });

  const handleUpdateStatus = (event: any) => {
    const formentry: any = { ...update };

    if (event.target.name in formentry) {

      formentry[event.target.name] = event.target.value;
    }
    setUpdate(formentry);
  };

  // const handleMultiSelectChange = (event: string) => {
  //   const formentry: any = { ...update };
  //   formentry['asset_component'] = event
  //   setUpdate(formentry);
  // };

  const handleChangeQuickUpdate = (event: any) => {
    const formentry: any = { ...update };
    if (event.target.id in formentry) {
      formentry[event.target.id] = event.target.value;
    }
    setUpdate(formentry);
  };

  const fetchProjectsAndData = async () => {
    isMounted.current = true;
    setLoader(true);
    const url = `/method/preflet_asset.apis.project.routes.projects?start=0&limit=50`;
    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };
    try {
      const res = await axios(config);
      const prjs = res?.data?.message || [];
      // Handle either list of strings or list of objects
      const projectNames = prjs.map((p: any) => (typeof p === 'string' ? p : p.name)).filter((p: any) => p !== null && p !== undefined);
      if (isMounted.current) {
        setProjectList(projectNames);
        getData(0, searchText, statusFilter, projectNames, false);
      }
    } catch (err) {
      console.log(err);
      if (isMounted.current) {
        getData(0, searchText, statusFilter, [], false);
      }
    }
  };

  const getData = (currentPage: number, search: string, status: string, projects: any[], isAppend = false) => {
    isMounted.current = true; // Set isMounted to true

    let filters = '';
    const filterArray = [];
    if (search) {
      filterArray.push(`["name","like","%${search}%"]`);
    }
    if (status) {
      filterArray.push(`["status","=","${status}"]`);
    }
    if (projects && projects.length > 0) {
      filterArray.push(`["project","in",${JSON.stringify(projects)}]`);
    }

    // Default filter for non-deleted or specific views if needed
    if (filterArray.length > 0) {
      filters = `&filters=[${filterArray.join(',')}]`;
    }

    const url = `/resource/Asset?fields=["name","status"]&limit_page_length=10&limit_start=${currentPage * 10}${filters}`;
    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };
    if (isMounted.current && !isAppend) setLoader(true);

    axios(config)
      .then((res: any) => {
        if (isMounted.current) {
          const newData = res?.data?.data || [];
          if (isAppend) {
            setHighlights((prev: any) => [...prev, ...newData]);
          } else {
            setHighlights(newData);
          }
          if (newData.length < 10) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        if (isMounted.current) {
          setLoader(false);
        }
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(0);
    getData(0, value, statusFilter, projectList, false);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
    getData(0, searchText, value, projectList, false);
  };

  const loadMoreData = (ev: any) => {
    if (hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      getData(nextPage, searchText, statusFilter, projectList, true);
    }
    setTimeout(() => ev.target.complete(), 500);
  };

  useEffect(() => {
    fetchProjectsAndData();
    // Cleanup function
    return () => {
      isMounted.current = false;
    };
  }, []);

  useIonViewWillLeave(() => {
    if (fabModal) {
      setFabModal(false);
    }
    setSearchText('');
    setStatusFilter('');
    setPage(0);
  });

  const modalFunc = async (param: any) => {
    setFabModal(true);
    // const Data = [..._data];
    // const acname = Data.findIndex((d) => d.field === 'asset_component');
    // Data[acname].options = await getAssetComponentByLabel();
    // set_Data(Data);

    const config: any = {
      method: 'get',
      url: `resource/Asset/${param}`,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };

    try {
      const statusData = await axios(config)
      // const assetComponent: { asset_component: any; }[] = []
      // statusData?.data?.data?.asset_component.map((item: any) => {
      //   assetComponent.push(item?.asset_component)
      // })
      setPrimaryData(statusData?.data?.data);

      for (const x in highlights) {

        if (highlights[x].name === param) {
          const formentry: any = { ...update };
          formentry.status = statusData?.data?.data?.status
          formentry.asset_state = statusData?.data?.data?.asset_state
          formentry.brand = statusData?.data?.data?.brand
          formentry.model = statusData?.data?.data?.model
          formentry.year = statusData?.data?.data?.year
          formentry.comment = statusData?.data?.data?.comment
          formentry.attachment.value = statusData?.data?.data?.attachment
          formentry.barcode_id = statusData?.data?.data?.barcode_id
          // formentry.asset_component = assetComponent
          setUpdate(formentry);
        }
      }
    } catch (error: any) {
      console.log(error)
    }
  }

  const SubmitStatusUpdate = async (e: any) => {
    e.preventDefault();
    hitQSupdate();
  };

  const hitQSupdate = async () => {
    const formData = new FormData();
    const temp: any = { ...update }
    for (const item in temp) {

      if (item === 'attachment' && temp['attachment']?.src) {
        try {

          const response = await fetch(temp['attachment']?.src?.toString());
          const blob = await response.blob();
          formData.append(
            'attachment',

            new File([blob], `${pname}-attachment.${temp['attachment'].format}`, {

              type: `image/${temp['attachment'].format}`,
            })
          );
        } catch (e: any) {
          console.log(e)
        }
      }
      // else if (item === 'asset_component') {
      //   const assComp: any = [] as any;
      //   if (temp[item].length > 0) {
      //     for (const element in temp['asset_component']) {
      //       assComp.push({
      //         asset_component: temp['asset_component'][element]
      //       })
      //     }
      //   }

      //   if (!checkIfArraysAreSame(primaryData[item], assComp))
      //     formData.append('asset_component', JSON.stringify(assComp));
      // }
      else if (item === 'attachment' && (primaryData[item] !== temp[item].value)) {

        formData.append('attachment', temp[item].value);

      } else if (temp[item] !== '' && temp[item] !== null && temp[item] !== undefined && temp[item] !== 'undefined') {

        if ((primaryData[item] !== temp[item]) && item !== 'attachment')

          formData.append(item, temp[item]);
      }
    }

    const config: any = {
      method: 'put',
      url: `method/preflet_asset.api.update_asset/${pname}`,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
      data: formData,
    };
    if (!isFormDataEmpty(formData)) {
      // Update status api
      try {
        setLoaderM(true);
        await axios(config);
      } catch (error: any) {
        console.log(error);
      }

      // Update comment
      try {
        setLoaderM(true);
        const config_comment: any = {
          method: 'post',
          url: 'resource/Comment',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
          },
          data: {
            comment_type: 'Comment',
            reference_doctype: 'Asset',
            reference_name: pname,
            content: update.comment,
          },
        };
        await axios(config_comment);
      } catch (error: any) {
        console.log(error);
      } finally {
        setLoaderM(false);
        setFabModal(false);
        present({
          message: t('Assets.CreateA.assetUpdateSuccesss'),
          duration: 2000,
        });
        // reset modal
        setUpdate({
          status: '',
          asset_state: '',
          brand: '',
          model: '',
          year: '',
          comment: '',
          attachment: {},
          barcode_id: '',
        });
        setPage(0);
        getData(0, searchText, statusFilter, projectList, false);
      }
    } else {
      setFabModal(false);
    }
  }



  const takeQSPicture = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 70,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      });
      const imageUrl = image.webPath;

      // Can be set to the src of an image now
      const formentry: any = { ...update }
      formentry['attachment'].value = null;
      formentry['attachment'].src = imageUrl;
      formentry['attachment'].format = image.format;
      setUpdate(formentry);
    } catch (e: any) {

      console.log('Error: ', e);

      if (e.toString().includes('cancelled')) {

        present({
          message: t('Utils.cameraCancel'),
          color: 'danger',
          duration: 2000,
        });
      }

      else if (e.toString().includes('denied') || e.includes('access')) {

        present({
          message: t('Utils.cameraErrorPermission'),
          color: 'danger',
          duration: 2000,
        });
      } else {

        present({
          message: e,
          color: 'danger',
          duration: 2000,
        });
      }
    }
  };

  /* Remove Quick Status Image by cross click */
  const emptyQSImg = () => {
    const tempData = { ...update };
    tempData.attachment = {};
    setUpdate(tempData);
  };

  const getScanner = async (field: any) => {
    const info = await Device.getInfo();
    if (info?.platform === 'web') {
      present({
        message: t('Utils.scannerNotSupported'),
        duration: 2000,
      });
      return
    }
    try {
      BarcodeScanner.isSupported().then((result) => {
        if (!result.supported) {
          present({
            message: t('Utils.unsupportDevice'),
            duration: 2000,
          });
        }
      });

      const granted = await requestPermissions();
      if (!granted) {
        present({
          message: t('Utils.noPermission'),
          duration: 2000,
        });
        return;
      }
      const check = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable()
      if (!check.available)
        BarcodeScanner.installGoogleBarcodeScannerModule()
      const { barcodes } = await BarcodeScanner.scan();
      const scannedValue = barcodes[0]?.displayValue
      handleScan(scannedValue, field);
    } catch (error: any) {
      console.log(error)
    }
  };

  const handleScan = (data: any, field: any) => {
    if (data) {
      try {
        const tempData = { ...update };
        tempData[field] = data;
        // tempData.barcode_id = data;
        setUpdate(tempData);
      } catch {
        present({
          message: t('Utils.fetchFail'),
          color: 'danger',
          duration: 4000,
        });
        console.log('failed sending id to the parent');
      } finally {
        // setScanToggle(false);
      }
    }
  };

  const openCreateAsset = () => {
    localStorage.setItem(constant.QUICK_SCAN_ASSET_CODE, '');
    router.push('/createAsset');
  };

  return (
    <IonPage>
      <Header enableBackButton="" title="" />
      <IonContent>

        {/* ── Hero Banner ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)',
          padding: '16px 16px 16px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          {/* Title */}
          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', position: 'relative', zIndex: 1, marginBottom: '1rem' }}>
            {t('Assets.Assets')}
          </p>
          {/* <h1 style={{ margin: '3px 0 12px', fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.15, position: 'relative', zIndex: 1 }}>
            Assets
          </h1> */}

          {/* Quick Action Buttons inside banner */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, position: 'relative', zIndex: 1 }}>
            {!!state.permissions.assets_create && (
              <div
                onClick={() => openCreateAsset()}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 14, padding: '10px 16px', boxShadow: '0 4px 16px rgba(29,80,141,0.15)', border: '1.5px solid rgba(29,80,141,0.1)', cursor: 'pointer', flexShrink: 0 }}
              >
                <img src={CreateAsset} alt="create" style={{ width: 18, height: 18 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1d508d', whiteSpace: 'nowrap' }}>{t('Assets.Create')}</span>
              </div>
            )}
            {!!state.permissions.assets_create && (
              <Link to="/createComp" style={{ textDecoration: 'none', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 14, padding: '10px 16px', boxShadow: '0 4px 16px rgba(29,80,141,0.15)', border: '1.5px solid rgba(29,80,141,0.1)', cursor: 'pointer' }}>
                  <img src={CreateComp} alt="component" style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1d508d', whiteSpace: 'nowrap' }}>{t('Component.Create')}</span>
                </div>
              </Link>
            )}
            <Link to="/compDetails" style={{ textDecoration: 'none', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 14, padding: '10px 16px', boxShadow: '0 4px 16px rgba(29,80,141,0.15)', border: '1.5px solid rgba(29,80,141,0.1)', cursor: 'pointer' }}>
                <img src={ViewComp} alt="view" style={{ width: 18, height: 18 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1d508d', whiteSpace: 'nowrap' }}>{t('Component.View')}</span>
              </div>
            </Link>
            <Link to="/all" style={{ textDecoration: 'none', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '10px 16px', border: '1.5px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                <img src={Clock} alt="history" style={{ width: 16, height: 16, filter: 'brightness(10)' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{t('Assets.history')}</span>
              </div>
            </Link>
          </div>
        </div>

        {/* ── Search + Status Filter ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px 4px' }}>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => handleSearchChange(e.detail.value!)}
            placeholder={t('Assets.searchAssets')}
            style={{ flex: 1, '--border-radius': '14px', '--box-shadow': '0 2px 8px rgba(0,0,0,0.06)', '--background': '#fff', padding: 0 } as any}
          />
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            background: '#fff', borderRadius: 14,
            border: '1.5px solid rgba(29,80,141,0.2)',
            paddingLeft: 8, paddingRight: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            flexShrink: 0,
          }}>
            <IonSelect
              value={statusFilter}
              placeholder={t('Assets.filterByStatus')}
              onIonChange={(e) => handleStatusChange(e.detail.value)}
              style={{ '--color': '#1d1d1d', '--placeholder-color': '#888', '--placeholder-opacity': 1, fontSize: 13, width: 'auto' } as any}
            >
              <IonSelectOption value="">{t('Assets.allStatuses')}</IonSelectOption>
              <IonSelectOption value="Operational">Operational</IonSelectOption>
              <IonSelectOption value="Installed But Not Operational">Installed But Not Operational</IonSelectOption>
              <IonSelectOption value="Need Repairing">Need Repairing</IonSelectOption>
              <IonSelectOption value="In Progress">In Progress</IonSelectOption>
              <IonSelectOption value="Need Replacement">Need Replacement</IonSelectOption>
              <IonSelectOption value="Replaced">Replaced</IonSelectOption>
              <IonSelectOption value="Need Installation">Need Installation</IonSelectOption>
              <IonSelectOption value="Inactive">Inactive</IonSelectOption>
              <IonSelectOption value="Need Maintenance">Need Maintenance</IonSelectOption>
            </IonSelect>
          </div>
        </div>

        {/* ── Section Header ── */}
        <div style={{ padding: '8px 16px 4px' }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1d508d' }}>{t('Assets.Assets')}</p>
        </div>

        {/* ── Skeleton Loaders ── */}
        {loader && [1, 2, 3, 4].map((el) => (
          <div key={el} style={{ margin: '0 12px 10px', background: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <IonSkeletonText animated style={{ width: '55%', height: 16, borderRadius: 8 }} />
              <IonSkeletonText animated style={{ width: '20%', height: 16, borderRadius: 8 }} />
            </div>
            <IonSkeletonText animated style={{ width: '35%', height: 12, borderRadius: 8 }} />
          </div>
        ))}

        {/* ── Asset Cards ── */}
        <div style={{ padding: '0 12px' }}>
          {highlights?.map((element: any, key: number) => (
            <div key={key} style={{
              background: '#fff',
              borderRadius: 18,
              padding: '14px 16px',
              marginBottom: 10,
              boxShadow: '0 3px 14px rgba(0,0,0,0.07)',
              border: '1.5px solid rgba(29,80,141,0.07)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              {/* Left: name + status */}
              <Link
                to={{ pathname: '/assetDetail', state: element.name, search: 'back=assets' }}
                style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}
              >
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1d2d44', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {element.name}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: getStatusColor(element.status), flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: getStatusColor(element.status) }}>
                    {t(`Utils.${element.status}`)}
                  </span>
                </div>
              </Link>

              {/* Right: quick-edit button */}
              {!!state.permissions.assets_update && (
                <div
                  onClick={() => { modalFunc(element.name); setPname(element.name); }}
                  style={{
                    flexShrink: 0,
                    marginLeft: 12,
                    width: 36, height: 36,
                    borderRadius: '50%',
                    background: 'rgba(29,80,141,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <IonIcon src={constructOutline} style={{ color: '#1d508d', fontSize: 17 }} />
                </div>
              )}
            </div>
          ))}

          <IonInfiniteScroll onIonInfinite={loadMoreData} threshold="100px" disabled={!hasMore}>
            <IonInfiniteScrollContent loadingSpinner="bubbles" loadingText={t('Assets.loadingMoreAssets')} />
          </IonInfiniteScroll>
        </div>

        {/* ── Empty State ── */}
        {!loader && highlights.length === 0 && (
          <IonRow>
            <NoRecords width="30%" smallText={true} />
          </IonRow>
        )}

        {/* ── Quick-Edit Modal (unchanged) ── */}
        <IonModal isOpen={fabModal} onDidDismiss={() => setFabModal(false)}>
          <IonHeader>
            <IonToolbar color="primary">
              &nbsp; {pname}
              <IonButtons slot="end">
                <IonButton className="line-button" onClick={() => setFabModal(false)}>
                  <IonIcon icon={close} slot="icon-only" />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonCardContent style={{ overflowY: 'scroll' }}>
            <IonLoading isOpen={loaderM} onDidDismiss={() => setLoaderM(false)} message={t('Utils.pleasewait')} />
            <form onSubmit={(e) => SubmitStatusUpdate(e)}>
              {_data.map((form_field: any, index: any) => {
                if (form_field.field === 'status' || form_field.field === 'asset_state') {
                  return (
                    <div key={index} className="input-container">
                      <label className="label">{t(`Assets.CreateA.${form_field.field}`)}{form_field.required ? '*' : null}</label>
                      <Select className="custom-select" required={form_field.required} id={`${form_field.field}_choice`} name={form_field.field} onChange={handleUpdateStatus} value={update[form_field.field]} disabled={form_field.disabled || false} key={`${form_field.field}-${index}`}>
                        {form_field.options?.map(function (opt: any, index: any) {
                          return (
                            <MenuItem value={`${opt}`} key={`${form_field.field}-${index}`}>
                              <section key={`${form_field.field}-${index}`} style={{ display: 'flex', height: '1rem' }}>
                                <p key={`${form_field.field}-${index}`} style={{ color: 'rgba(31, 87, 148, 1)', marginLeft: '0.2rem', fontSize: '12px', marginTop: '0px' }}>
                                  {form_field.translate ? t(`Assets.CreateA.${opt}`) : opt}
                                </p>
                              </section>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </div>
                  );
                } else if (form_field.field === 'brand' || form_field.field === 'model' || form_field.field === 'year') {
                  return (
                    <div key={index} className="input-container">
                      <label className="label">{t(`Assets.CreateA.${form_field.field}`)}{form_field.required ? '*' : null}</label>
                      <input className="basic-input" required={false} key={`${form_field.field}-${index}`} id={form_field.field} placeholder={t(`Assets.CreateA.${form_field.field}`)} onChange={handleChangeQuickUpdate} value={update[form_field.field]} />
                    </div>
                  );
                } else if (form_field.type === 'file') {
                  return (
                    <div id={`${form_field.field}-${index}`} className="input-container" key={`${form_field.field}-${index}`}>
                      <label id={`label-${form_field.field}`} className="label">{t(`Assets.CreateA.${form_field.field}`)}{form_field.required ? '*' : null}{' '}</label>
                      <IonButton style={{ marginTop: '1rem', margin: 'auto', width: '40%', color: 'black', marginBottom: '1rem' }} size="small" color="light" onClick={() => takeQSPicture()}>
                        {update[form_field.field]['value'] ? t('Utils.change_image') : t('Utils.image')}
                      </IonButton>
                      <div className="flex justify-center items-center">
                        {update[form_field.field]['value'] ? (
                          <img src={`${localStorage.getItem('baseUrl')?.replace('/api', '')}${update[form_field.field]['value']}`} alt="" style={{ width: '60%', margin: 'auto', display: 'block', border: '1px solid lightgray', borderRadius: '12px' }} />
                        ) : ''}
                        {update['attachment']['src'] ? <img className="preview-survey-img" src={update['attachment']['src']} alt="" /> : ''}
                        {update['attachment']['src'] ? (<IonIcon className="close-icon" onClick={() => emptyQSImg()} src={close} />) : ''}
                      </div>
                    </div>
                  );
                } else if (form_field.type === 'barcode') {
                  return (
                    <div id={`${form_field.field}-${index}`} className="input-container" key={`${form_field.field}-${index}`}>
                      <label id={`label-${form_field.field}`} style={{ marginTop: '1rem' }} className="label">{t(`Assets.CreateA.${form_field.field}`)}{form_field.required ? '*' : null}</label>
                      <div className="flex items-center justify-between basic-input">
                        <input required={form_field.required} className="gps-input" id={form_field.field} placeholder={t(`Assets.CreateA.${form_field.field}`)} value={update[form_field.field]} disabled={true} style={{ width: '80%' }} />
                        <IonIcon onClick={() => getScanner(form_field.field)} className="gps-icon" src={barcodeOutline} />
                      </div>
                    </div>
                  );
                } else { return ''; }
              })}
              <div className="input-container">
                <label className="label">{t(`Utils.Comment`)}</label>
                <textarea className="textarea" required={false} key="comment" id="comment" placeholder={t('Utils.Add your comment')} onChange={handleChangeQuickUpdate} value={update.comment} />
              </div>
              <button className="submit-button" type="submit">{t(`Assets.CreateA.update`)}</button>
            </form>
          </IonCardContent>
        </IonModal>

        <ScanButton isAssetPage={true} />
      </IonContent>
    </IonPage>
  );
};

export default Assets;

